import React, { forwardRef, useState } from "react";
import { FaDownload, FaFileWord } from "react-icons/fa";
import AudioMessage from "./AudioMessage";

const WhatsAppConversation = forwardRef(
  (
    { messages, setCurrentPage, paginationData, selectedCustomer },
    messagesEndRef
  ) => {
    const [isModalOpen, setModalOpen] = useState(false);
    const increment = () => {
      setCurrentPage((prev) => prev + 1);
    };

    // for long msg
    const [isExpanded, setIsExpanded] = useState(false);
    const maxLength = 250; // adjust how many chars to show initially

    return (
      <div className="space-y-2">
        {selectedCustomer && (
          <div className="flex justify-center">
            <span
              onClick={() => {
                if (
                  paginationData?.current_page !== paginationData?.last_page
                ) {
                  increment();
                  messagesEndRef.current?.scrollIntoView({
                    behavior: "smooth",
                  });
                }
              }}
              className={`rounded bg-gray-300 px-5 py-1 text-sm text-black transition-colors 
      ${
        paginationData?.current_page === paginationData?.last_page
          ? "cursor-not-allowed opacity-60"
          : "cursor-pointer hover:bg-gray-400"
      }`}
            >
              {messages.length === 0
                ? "No chat available"
                : paginationData?.current_page === paginationData?.last_page
                ? "No more messages"
                : "Load more messages"}
            </span>
          </div>
        )}

        {messages.map((msg, index) => {
          const isLast = index === 0;
          const isLong = msg?.content.length > maxLength;
          const displayedText = isExpanded
            ? msg?.content
            : msg?.content.slice(0, maxLength);

          return (
            <div
              key={msg.id || index}
              ref={isLast ? messagesEndRef : null}
              className={`flex ${
                msg.direction === "inbound" ? "justify-start" : "justify-end"
              }`}
            >
              <div
                className={`max-w-xs rounded-lg px-3 py-2 shadow-sm lg:max-w-md ${
                  msg.direction === "inbound"
                    ? "bg-white dark:bg-[#262d31]"
                    : "bg-[#005c4b] text-white dark:bg-[#005c4b]"
                } ${
                  msg.direction === "inbound"
                    ? "rounded-tl-none"
                    : "rounded-tr-none"
                }`}
              >
                {/* Image message */}
                <div>
                  {msg.file_url && msg.file_type.startsWith("image") && (
                    <div className="relative inline-block">
                      <img
                        src={msg.file_url}
                        alt="media"
                        className="mt-1 max-w-full cursor-pointer rounded-lg"
                        loading="lazy"
                        onClick={() => window.open(msg.file_url, "_blank")}
                      />
                    </div>
                  )}
                </div>

                {/* Word document */}
                <div>
                  {msg.file_url &&
                    msg.file_type ===
                      "application/vnd.openxmlformats-officedocument.wordprocessingml.document" && (
                      <a
                        href={msg.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-3 rounded-lg border px-4 py-2 shadow-sm transition hover:shadow-md ${
                          msg.direction === "inbound"
                            ? "border-blue-300 text-blue-600"
                            : "border-gray-300 text-gray-500"
                        }`}
                      >
                        {/* Word icon */}
                        <FaFileWord className="h-6 w-6 text-blue-600" />
                        {/* Label */}
                        <span className="font-semibold">Word Document</span>
                      </a>
                    )}
                </div>

                {/* PDF file */}
                {msg.file_url && msg.file_type && (
                  <div className="mt-1">
                    {msg.file_type === "application/pdf" ? (
                      <a
                        href={msg.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`flex items-center gap-2 rounded-lg border px-4 py-2 shadow-sm transition hover:bg-red-50 hover:shadow-md
    ${
      msg.direction === "inbound"
        ? "border-red-300 text-red-600"
        : "border-gray-300 text-white"
    }`}
                      >
                        {/* PDF icon */}
                        <svg
                          className="h-6 w-6 text-red-500"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zM14 3.5L19.5 9H14V3.5z" />
                          <text
                            x="6"
                            y="18"
                            fontSize="8"
                            fontWeight="bold"
                            fill="white"
                          >
                            PDF
                          </text>
                        </svg>

                        {/* Label */}
                        <span className="font-semibold">PDF Document</span>
                      </a>
                    ) : msg.file_type ===
                      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ? (
                      <a
                        href={msg.file_url}
                        download
                        className={`flex items-center gap-3 rounded-lg border border-green-300 bg-green-50 px-4 py-2 shadow-sm hover:bg-green-100 ${
                          msg.direction === "inbound"
                            ? "text-green-700"
                            : "text-green-500"
                        }`}
                      >
                        {/* Excel icon */}
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-6 w-6 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 2H8a2 2 0 0 0-2 2v3H3v10h3v3a2 2 0 0 0 2 2h11l4-4V4a2 2 0 0 0-2-2zM8 19v-3h7v3H8zm7-5H8v-3h7v3zm0-5H8V6h7v3z" />
                        </svg>

                        <span className="font-medium">Excel File</span>
                      </a>
                    ) : null}
                  </div>
                )}

                {/* Video message */}
                {msg?.file_url && msg?.file_type === "video/mp4" && (
                  <div className="mt-1">
                    <video
                      controls
                      className="max-w-full rounded-lg"
                      style={{ maxHeight: "300px" }}
                    >
                      <source src={msg.file_url} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                )}
                {/* Audio message */}

                {msg?.file_url && msg?.file_type.startsWith("audio/") && (
                  <AudioMessage fileUrl={msg.file_url} />
                )}

                {/* Location message */}
                {msg?.content?.includes("Location:") ? (
                  <div
                    className={`mt-1 w-full max-w-sm rounded-lg p-3 ${
                      msg.direction === "inbound"
                        ? "border-gray-200 bg-white dark:border-gray-700 dark:bg-[#262d31]"
                        : "bg-[#056162]"
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div
                        className={`rounded-full p-2 ${
                          msg.direction === "inbound"
                            ? "bg-gray-100 dark:bg-gray-700"
                            : "bg-[#067d76]"
                        }`}
                      >
                        <svg
                          className="h-6 w-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                          />
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                          />
                        </svg>
                      </div>
                      <div>
                        <div
                          className={`font-semibold ${
                            msg.direction === "inbound"
                              ? "text-gray-800 dark:text-white"
                              : "text-white"
                          }`}
                        >
                          Shared Location
                        </div>
                        <div
                          className={`text-xs ${
                            msg.direction === "inbound"
                              ? "text-gray-600 dark:text-gray-300"
                              : "text-[#99beb7]"
                          }`}
                        >
                          Google Maps
                        </div>
                      </div>
                    </div>

                    <a
                      href={msg?.content?.split("Location: ")[1]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`mt-2 block break-all font-medium underline ${
                        msg.direction === "inbound"
                          ? "text-blue-600 dark:text-blue-400"
                          : "text-[#99beb7]"
                      }`}
                    >
                      View on Maps
                    </a>
                  </div>
                ) : (
                  <p
                    className={`break-words text-sm ${
                      msg.direction === "inbound"
                        ? "text-gray-800 dark:text-gray-100"
                        : "text-white"
                    }`}
                  >
                    {displayedText}
                    {isLong && !isExpanded && (
                      <span
                        onClick={() => setIsExpanded(true)}
                        className="ml-1 cursor-pointer text-blue-500"
                      >
                        Read more
                      </span>
                    )}
                    {isLong && isExpanded && (
                      <span
                        onClick={() => setIsExpanded(false)}
                        className="ml-1 cursor-pointer text-blue-500"
                      >
                        Show less
                      </span>
                    )}
                  </p>
                )}

                {/* Message metadata */}
                <div className="relative mt-1 flex items-center justify-end gap-1">
                  <div className="group relative inline-flex">
                    <span
                      className={`text-xs ${
                        msg.direction === "inbound"
                          ? "text-gray-500 dark:text-gray-400"
                          : "text-[#99beb7]"
                      }`}
                    >
                      {new Date(msg?.created_at).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>

                    {/* Hover timestamp */}
                    <div
                      className={`absolute bottom-full mb-1 hidden w-max rounded-lg bg-[#111b21] px-3 py-2 text-xs text-white opacity-90 shadow-lg group-hover:block
      ${msg?.direction === "outbound" ? "right-0" : "left-0"}`}
                    >
                      <div className="flex flex-col gap-1">
                        {msg.sent_at && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-300">Sent:</span>
                            <span>
                              {new Date(msg.sent_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        )}
                        {msg.read_at && (
                          <div className="flex items-center gap-2">
                            <span className="text-gray-300">Read:</span>
                            <span>
                              {new Date(msg.read_at).toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </span>
                          </div>
                        )}
                      </div>
                      <div
                        className={`absolute -bottom-1 h-2 w-2 rotate-45 bg-[#111b21] 
        ${msg?.direction === "outbound" ? "right-2" : "left-2"}`}
                      ></div>
                    </div>
                  </div>

                  {/* Read receipts */}
                  {msg.direction === "outbound" && (
                    <span className="ml-1">
                      {msg.status === "sent" && (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          className="text-gray-400"
                        >
                          <path d="M3 8.5L6 11.5L13 4.5" />
                        </svg>
                      )}
                      {msg.status === "delivered" && (
                        <div className="flex">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="-ml-1 text-gray-400"
                          >
                            <path d="M3 8.5L6 11.5L13 4.5" />
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            className="-ml-1 text-gray-400"
                          >
                            <path d="M3 8.5L6 11.5L13 4.5" />
                          </svg>
                        </div>
                      )}
                      {msg.status === "read" && (
                        <div className="flex">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="#53bdeb"
                            strokeWidth="2"
                          >
                            <path d="M3 8.5L6 11.5L13 4.5" />
                          </svg>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            stroke="#53bdeb"
                            strokeWidth="2"
                            className="-ml-1"
                          >
                            <path d="M3 8.5L6 11.5L13 4.5" />
                          </svg>
                        </div>
                      )}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }
);

export default WhatsAppConversation;
